import React, { useState } from "react";

import { useAccount } from "@/api/accounts";
import { Popover, Spinner, Stack, Text } from "@/components/ui";
import ActionButton from "@/features/ui/components/action-button";
import UserPanel from "@/features/ui/components/user-panel";
import { useIsTouchDevice } from "@/hooks";

import type { Account } from "@/schemas";

interface AccountPopoverProps {
  children: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  isEnabled?: boolean;
  account: Pick<Account, "id">;
}

const AccountPopover = (props: AccountPopoverProps) => {
  const {
    children,
    isEnabled = true,
    account: { id },
  } = props;

  const isTouchDevice = useIsTouchDevice();

  const [isOpen, setIsOpen] = useState(false);
  const { account, isLoading } = useAccount(id, {
    refetch: true,
    isEnabled: isEnabled && isOpen,
  });

  if (isTouchDevice || !isEnabled) {
    return children;
  }

  return (
    <Popover
      interaction="hover"
      referenceElementClassName="cursor-pointer"
      onOpen={() => setIsOpen(true)}
      content={
        <Stack
          space={4}
          className="relative isolate w-80 overflow-hidden rounded-lg p-4"
          data-testid="account-popover"
        >
          {isLoading || !account ? (
            <Spinner />
          ) : (
            <>
              <UserPanel
                accountId={account.id}
                action={<ActionButton account={account} buttonSize="sm" />}
              />

              {account.note.length > 0 && (
                <Text
                  size="sm"
                  tag="div"
                  className="line-clamp-5"
                  dangerouslySetInnerHTML={{ __html: account.note_emojified }}
                />
              )}
            </>
          )}
        </Stack>
      }
      isFlush
      children={<div className="inline-block max-w-full">{children}</div>}
    />
  );
};

export default AccountPopover;
